import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Eye, EyeOff, Library, Lock, Pencil, Plus } from "lucide-react";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Card, ConfirmDelete, Empty, ErrorBox, FormSheet, Loading, PageTitle } from "@/components/common";
import { Field } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const copy = async (text, what, done = "copiato") => {
  try { await navigator.clipboard.writeText(text); toast.success(`${what} ${done}`); } catch { toast.error("Impossibile copiare"); }
};

function PortalLogo({ p }) {
  const api = useApi();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (p.has_logo) {
      api("get", `/portals/${p.id}/logo`, null, { responseType: "blob" })
        .then((b) => { url = URL.createObjectURL(b); setSrc(url); })
        .catch(() => setSrc(null));
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [p.id, p.has_logo, api]);
  return (
    <span className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-2xl border border-border bg-white" data-testid={`portal-logo-${p.id}`}>
      {src ? <img src={src} alt={p.name} className="h-full w-full object-contain p-1.5" /> : <span className="font-heading text-xl font-extrabold text-primary">{p.name[0]?.toUpperCase()}</span>}
    </span>
  );
}

function PortalSheet({ open, onOpenChange, portal, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState({ name: "", url: "", username: "", password: "" });
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setForm({ name: portal?.name || "", url: portal?.url || "", username: portal?.username || "", password: "" }); setLogo(null); }
  }, [open, portal]);
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) return toast.error("Inserisci nome e link");
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("url", form.url);
    fd.append("username", form.username);
    if (!portal || form.password) fd.append("password", form.password);
    if (logo) fd.append("logo", logo);
    setBusy(true);
    try {
      await api(portal ? "put" : "post", portal ? `/portals/${portal.id}` : "/portals", fd);
      toast.success(portal ? "Portale aggiornato" : "Portale aggiunto");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title={portal ? "Modifica portale" : "Nuovo portale"} description="Visibile a tutta la classe. La password è salvata cifrata.">
      <form onSubmit={save} className="space-y-4" data-testid="portal-form">
        <Field label="Nome del portale"><Input className="h-11" value={form.name} placeholder="Es. MyZanichelli" onChange={set("name")} data-testid="portal-name" /></Field>
        <Field label="Link"><Input className="h-11" type="text" inputMode="url" autoCapitalize="off" value={form.url} placeholder="https://my.zanichelli.it" onChange={set("url")} data-testid="portal-url" /></Field>
        <Field label="Username / email"><Input className="h-11" autoComplete="off" value={form.username} onChange={set("username")} data-testid="portal-username" /></Field>
        <Field label="Password" hint={portal ? "Lascia vuoto per non cambiarla" : undefined}>
          <Input className="h-11" type="password" autoComplete="new-password" value={form.password} onChange={set("password")} data-testid="portal-password" />
        </Field>
        <Field label="Logo (facoltativo)" hint="Immagine, max 2 MB">
          <Input type="file" accept="image/*" className="h-11 pt-2" onChange={(e) => setLogo(e.target.files?.[0] || null)} data-testid="portal-logo-input" />
        </Field>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="portal-submit">{busy ? "Salvataggio…" : "Salva"}</Button>
      </form>
    </FormSheet>
  );
}

function PortalCard({ p, isAdmin, onEdit, onDelete }) {
  const api = useApi();
  const [pwd, setPwd] = useState(null);
  const getPwd = async () => pwd ?? (await api("get", `/portals/${p.id}/password`)).password;
  const reveal = async () => {
    if (pwd !== null) return setPwd(null);
    try { setPwd(await getPwd()); } catch (e) { toast.error(errMsg(e)); }
  };
  const copyPwd = async () => {
    try { await copy(await getPwd(), "Password", "copiata"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="fade-up min-w-0" data-testid={`portal-card-${p.id}`}>
      <div className="flex items-start gap-3">
        <PortalLogo p={p} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-heading text-lg font-bold" data-testid={`portal-name-${p.id}`}>{p.name}</h3>
          <p className="truncate text-xs text-muted-foreground">{p.url}</p>
        </div>
        {isAdmin && <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onEdit} data-testid={`portal-edit-${p.id}`} aria-label="Modifica"><Pencil className="h-4 w-4" /></Button>}
        {isAdmin && <ConfirmDelete testId={`portal-delete-${p.id}`} title={`Eliminare ${p.name}?`} onConfirm={onDelete} />}
      </div>
      <div className="mt-3 space-y-2">
        {p.username && (
          <div className="flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2">
            <span className="w-20 shrink-0 text-xs font-semibold text-muted-foreground">Username</span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium" data-testid={`portal-username-${p.id}`}>{p.username}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copy(p.username, "Username")} data-testid={`portal-copy-username-${p.id}`} aria-label="Copia username"><Copy className="h-4 w-4" /></Button>
          </div>
        )}
        <div className="flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2">
          <span className="w-20 shrink-0 text-xs font-semibold text-muted-foreground">Password</span>
          <span className="min-w-0 flex-1 truncate font-mono text-sm" data-testid={`portal-password-${p.id}`}>{pwd ?? "••••••••"}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={reveal} data-testid={`portal-reveal-${p.id}`} aria-label="Mostra password">{pwd !== null ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={copyPwd} data-testid={`portal-copy-password-${p.id}`} aria-label="Copia password"><Copy className="h-4 w-4" /></Button>
        </div>
      </div>
      <Button asChild className="mt-3 h-11 w-full rounded-full" data-testid={`portal-open-${p.id}`}>
        <a href={p.url} target="_blank" rel="noopener noreferrer">Apri portale <ExternalLink className="ml-1.5 h-4 w-4" /></a>
      </Button>
    </Card>
  );
}

export default function Portali() {
  const api = useApi();
  const { isAdmin } = useMe();
  const { data, error, reload } = useFetch("/portals");
  const [editing, setEditing] = useState(null);
  if (error) return <ErrorBox text={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const remove = async (id) => {
    try { await api("delete", `/portals/${id}`); toast.success("Portale eliminato"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageTitle title="Libri digitali" subtitle="Portali e credenziali della classe"
        action={isAdmin && <Button className="rounded-full" onClick={() => setEditing("new")} data-testid="btn-add-portal"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>} />
      <p className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 shrink-0" />Credenziali di classe: le vedono solo i membri della classe. Solo i rappresentanti possono aggiungerle o modificarle.</p>
      {data.length === 0 ? <Empty icon={Library} text={isAdmin ? "Nessun portale. Tocca Aggiungi per inserire il primo." : "I rappresentanti non hanno ancora inserito portali."} testId="portals-empty" /> : (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.map((p) => <PortalCard key={p.id} p={p} isAdmin={isAdmin} onEdit={() => setEditing(p)} onDelete={() => remove(p.id)} />)}
        </div>
      )}
      <PortalSheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)} portal={editing === "new" ? null : editing} onSaved={reload} />
    </div>
  );
}
